/**
 * SF-164: population/employment/net-change pure logic. `computeArmy`/`computeUnfilledJobs`/
 * `getEmploymentStatus` are ported verbatim (parenthesization stripped, see
 * `getEmploymentStatus` below) out of the deleted `components/council/tabs/EconomyTab.tsx`.
 * `getNetChangeColor`/`formatNetValue` are shared-extracted (needed by both shells' Net
 * Changes/Trends rendering — not a per-shell duplicate, the `dragonPresentation.ts` precedent).
 * `getCapacityStatus`/`NET_CHANGE_GROUPS` are new.
 */
import type { Employment, Population, TickChanges, MonthlyAggregate } from '../types/province';
import { PILLAR_COLORS } from '../constants/pillarColors';
import { COLORS } from '../constants/theme';
import { formatNumber } from './format';

/** Moved here from `EconomyCards.tsx` (v1.3 fix) — a component file may only export components
 *  (`react-refresh/only-export-components`), mirroring `economyFlow.ts`'s `SOURCE_COLORS`. */
export const EC = PILLAR_COLORS.economy;

export function computeArmy(pop: Population): number {
  return pop.soldiers + pop.offenseSpecialists + pop.defenseSpecialists + pop.elites;
}

export function computeUnfilledJobs(emp: Employment): number {
  return Math.max(0, emp.totalJobs - emp.employed);
}

export interface StatusResult {
  color: string;
  label: string;
}

/**
 * v1.3 fix: bare label (`'Healthy'`, not `'(Healthy)'`) — the parenthetical wrapping now happens
 * at each platform's own render site (Components §2/§3), not here. Also gains the same
 * `Number.isFinite` guard as `getCapacityStatus`, returning `null` rather than trusting the DTO's
 * `employmentRate` is always a real number.
 */
export function getEmploymentStatus(rate: number): StatusResult | null {
  if (!Number.isFinite(rate)) return null;
  if (rate >= 0.9) return { color: COLORS.positive, label: 'Healthy' };
  if (rate >= 0.5) return { color: COLORS.warning, label: 'Low' };
  return { color: COLORS.negative, label: 'Critical' };
}

/**
 * SF-164 (v1.3 fix, Critical Issue #1): numerator is `capacityUnitCount`
 * (`province.getTotalUnitCount()`-mirroring, Application Interface), NOT `population.total` —
 * `total` excludes war horses/generals/the training queue that the engine's own overpopulation
 * check includes. Directionally inverted from `getEmploymentStatus`: a HIGH ratio is risk, not
 * health — don't port `getEmploymentStatus`'s threshold shape structurally (CLAUDE.md's recurring
 * "porting a function's shape to an inverted-direction consumer" pitfall, SF-165).
 *
 * Guard: pre-deploy Redis-cached DTOs have `maxPopulation === undefined`/
 * `capacityUnitCount === undefined`; a naive `maxPopulation <= 0` guard lets `undefined` slip
 * through (`undefined <= 0` is `false`) to `capacityUnitCount / undefined` → `NaN`, which would
 * otherwise fall to the `> 1.0`-else branch — the reassuring "Growing" label, exactly backwards
 * for an unknown/broken value. `Number.isFinite(...)` on both operands plus `maxPopulation > 0`
 * catches: a legitimate `0` (no explored land), `undefined` (pre-deploy cache), and a stray `NaN`
 * — in one guard (same shape as `HubCard.tsx`'s `ratioWidthPercent()` `max <= 0` rule, SF-167,
 * extended for `undefined`/`NaN`).
 *
 * `0.90` is a chosen early-warning band, not a mechanic boundary — the real growth-throttle onset
 * is `ratio >= 1.0` per `calculateBirths()`; `ratio > 1.0` is verified exact against
 * `calculateOverpopulation()` (`PopulationSystem.java:298`, strictly-greater-than).
 */
export function getCapacityStatus(
  capacityUnitCount: number,
  maxPopulation: number | undefined
): StatusResult | null {
  if (
    !Number.isFinite(capacityUnitCount) ||
    !Number.isFinite(maxPopulation) ||
    (maxPopulation as number) <= 0
  ) {
    return null;
  }
  const ratio = capacityUnitCount / (maxPopulation as number);
  if (ratio < 0.9) return { color: COLORS.text.secondary, label: 'Growing' };
  if (ratio <= 1.0) return { color: COLORS.warning, label: 'Near Capacity' };
  if (ratio > 1.0) return { color: COLORS.negative, label: 'Overpopulated' };
  // v1.3 fix (qa-engineer H3): explicit terminal else — closes the gap where a NaN numerator
  // (distinct from a NaN/missing maxPopulation, which the guard above already catches) could
  // fall off the end of the branch chain to an implicit `undefined`, contradicting the declared
  // `| null` return type. Unreachable given the guard above, but explicit rather than implicit.
  return null;
}

/**
 * Ported verbatim from `EconomyTab.tsx`.
 */
export function getNetChangeColor(value: number): string {
  if (value > 0) return COLORS.positive;
  if (value < 0) return COLORS.negative;
  return COLORS.text.tertiary;
}

/**
 * Ported verbatim from `EconomyTab.tsx`. NOT null-safe: `formatNetValue(undefined)` doesn't
 * throw — it silently returns `'0'` (the `> 0`/`< 0` branches both fail on `undefined`, falling
 * through to the `'0'` default). Every call site (both shells' Net Changes/Trends rows) must
 * replicate the `value != null ? formatNetValue(value) : '—'` ternary itself — see
 * `MobileEconomyShell`/`DesktopEconomyShell`. Emits a Unicode minus sign (`−`, U+2212), not ASCII
 * `-`, for negative values.
 */
export function formatNetValue(value: number): string {
  if (value > 0) return `+${formatNumber(Math.round(value))}`;
  if (value < 0) return `−${formatNumber(Math.round(Math.abs(value)))}`;
  return '0';
}

// ─── Net Changes / Trends shared descriptor ────────────────────────────────

export interface NetChangeRow {
  label: string;
  yesterday: (tc: TickChanges) => number;
  thisMonth: (m: MonthlyAggregate) => number | null;
  lastMonth: (m: MonthlyAggregate) => number | null;
}

export interface NetChangeGroup {
  group: 'Gold' | 'Population' | 'Food' | 'Runes';
  rows: NetChangeRow[];
  net: NetChangeRow;
}

/**
 * SF-164 (v1.3 fix, Recommended Change): shared descriptor driving BOTH desktop's unchanged
 * 4-column Net Changes table AND mobile's new Trends panels — replaces 14 independent
 * hand-written `DataTriple`/`NetRow` (desktop) / row (mobile) blocks with one data structure both
 * platforms iterate, so the two platforms can't independently drift on the label→field mapping.
 * Encodes the existing 14-row mapping verbatim, including the `Deaths & Desertions` composite
 * (`tc.deaths + tc.unitsDeserted`) and the two `null`-month accessor cases (Draft Costs/Science
 * Investment have no equivalent field on `MonthlyAggregate` — their `thisMonth`/`lastMonth`
 * accessors return `null` unconditionally, ignoring their `m` argument).
 */
export const NET_CHANGE_GROUPS: NetChangeGroup[] = [
  {
    group: 'Gold',
    rows: [
      { label: 'Our Income', yesterday: (tc) => tc.goldIncome, thisMonth: (m) => m.totalGoldIncome, lastMonth: (m) => m.totalGoldIncome },
      { label: 'Our Expenses', yesterday: (tc) => tc.goldWages, thisMonth: (m) => m.totalGoldWages, lastMonth: (m) => m.totalGoldWages },
      { label: 'Draft Costs', yesterday: (tc) => tc.draftGoldSpent, thisMonth: () => null, lastMonth: () => null },
      { label: 'Science Investment', yesterday: (tc) => tc.scienceGoldSpent, thisMonth: () => null, lastMonth: () => null },
    ],
    net: { label: 'Net Change', yesterday: (tc) => tc.goldNet, thisMonth: (m) => m.netGold, lastMonth: (m) => m.netGold },
  },
  {
    group: 'Population',
    rows: [
      { label: 'Births', yesterday: (tc) => tc.births, thisMonth: (m) => m.totalPopulationBirths, lastMonth: (m) => m.totalPopulationBirths },
      { label: 'Deaths & Desertions', yesterday: (tc) => tc.deaths + tc.unitsDeserted, thisMonth: (m) => m.totalPopulationDeaths, lastMonth: (m) => m.totalPopulationDeaths },
    ],
    net: { label: 'Net Change', yesterday: (tc) => tc.populationNet, thisMonth: (m) => m.netPopulation, lastMonth: (m) => m.netPopulation },
  },
  {
    group: 'Food',
    rows: [
      { label: 'Food Grown', yesterday: (tc) => tc.foodProduced, thisMonth: (m) => m.totalFoodProduced, lastMonth: (m) => m.totalFoodProduced },
      { label: 'Food Needed', yesterday: (tc) => tc.foodConsumed, thisMonth: (m) => m.totalFoodConsumed, lastMonth: (m) => m.totalFoodConsumed },
      { label: 'Food Decayed', yesterday: (tc) => tc.foodDecay, thisMonth: (m) => m.totalFoodDecayed, lastMonth: (m) => m.totalFoodDecayed },
    ],
    net: { label: 'Net Change', yesterday: (tc) => tc.foodNet, thisMonth: (m) => m.netFood, lastMonth: (m) => m.netFood },
  },
  {
    group: 'Runes',
    rows: [
      { label: 'Runes Produced', yesterday: (tc) => tc.runesProduced, thisMonth: (m) => m.totalRunesProduced, lastMonth: (m) => m.totalRunesProduced },
      { label: 'Runes Decayed', yesterday: (tc) => tc.runesDecayed, thisMonth: (m) => m.totalRunesDecayed, lastMonth: (m) => m.totalRunesDecayed },
    ],
    net: { label: 'Net Change', yesterday: (tc) => tc.runesNet, thisMonth: (m) => m.netRunes, lastMonth: (m) => m.netRunes },
  },
];
